import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import Error404 from 'components/common/Error404';
import { getVM } from 'helpers/requests/vms';
import { appPaths } from 'config';
import { handleUnexpectedStatusCode } from 'helpers/errors';
import { Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FileBrowserPanel from './FileBrowserPanel';
import OpenClawPanel from './OpenClawPanel';
import Playground from './Playground';

const VM = () => {
  const [loading, setLoading] = useState(true);
  const [vm, setVm] = useState({});
  const [notFound, setNotFound] = useState(false);
  const [suspended, setSuspended] = useState(false);
  const [panelPos, setPanelPos] = useState({ x: 0, y: 12 });
  const [panelReady, setPanelReady] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeView = searchParams.get('view') || 'screen';
  const setActiveView = view => {
    setSearchParams(view === 'screen' ? {} : { view }, { replace: true });
  };
  const { id } = useParams();
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Center panel on mount
  const initPanel = useCallback(node => {
    panelRef.current = node;
    if (node) {
      const rect = node.getBoundingClientRect();
      setPanelPos({ x: (window.innerWidth - rect.width) / 2, y: 12 });
      setPanelReady(true);
    }
  }, []);

  let handleGetVM = async () => {
    const r = await getVM(id);
    if (r.success) {
      setVm(r.response.data);
      if (r.response.data.status === 'suspended') {
        setSuspended(true);
      }
    } else {
      if (r.response.status === 404) {
        setNotFound(true);
      } else if (r.response.status === 403) {
        setSuspended(true);
      } else {
        handleUnexpectedStatusCode(r.expectedStatusCode, r.response.status);
      }
    }
    setLoading(false);
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  const viewsRef = useRef(null);

  const handlePanelMouseDown = e => {
    if (e.target.closest('button')) return;
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
    e.preventDefault();

    // Disable pointer events on iframes so they don't steal mousemove
    if (viewsRef.current) viewsRef.current.classList.add('pe-none');
    document.body.style.cursor = 'grabbing';
    el.style.cursor = 'grabbing';

    const handleMouseMove = ev => {
      const x = Math.max(
        0,
        Math.min(
          window.innerWidth - el.offsetWidth,
          ev.clientX - dragOffset.current.x
        )
      );
      const y = Math.max(
        0,
        Math.min(
          window.innerHeight - el.offsetHeight,
          ev.clientY - dragOffset.current.y
        )
      );
      el.style.left = x + 'px';
      el.style.top = y + 'px';
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      if (viewsRef.current) viewsRef.current.classList.remove('pe-none');
      document.body.style.cursor = '';
      el.style.cursor = 'grab';
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  useEffect(() => {
    handleGetVM();
  }, []);

  if (notFound) {
    return <Error404 />;
  }

  if (suspended) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: 'calc(100vh - 200px)' }}
      >
        <Card style={{ maxWidth: '500px', width: '100%' }}>
          <Card.Body className="text-center py-5">
            <FontAwesomeIcon
              icon="lock"
              className="text-danger mb-3"
              size="3x"
            />
            <h4>Cloud Desktop Suspended</h4>
            <p className="text-muted">
              This Cloud Desktop is suspended. Subscribe or resubscribe to
              restore access.
            </p>
            <div className="d-flex gap-2 justify-content-center">
              <Button
                variant="primary"
                onClick={() => navigate(appPaths.settings.billing)}
              >
                View plans
              </Button>
              <Button
                variant="falcon-default"
                onClick={() => navigate(appPaths.vms.list)}
              >
                Back to Cloud Desktops
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1050,
        backgroundColor: '#000'
      }}
    >
      {/* Draggable floating panel */}
      <div
        ref={initPanel}
        onMouseDown={handlePanelMouseDown}
        className="d-flex align-items-center gap-3 bg-light border rounded-3 px-3 py-2"
        style={{
          position: 'fixed',
          left: panelPos.x,
          top: panelPos.y,
          zIndex: 1060,
          opacity: panelReady ? 1 : 0,
          cursor: 'grab',
          userSelect: 'none'
        }}
      >
        <FontAwesomeIcon icon="grip-lines" className="text-secondary fs--2" />
        <Button
          variant="link"
          size="sm"
          className="p-0 text-decoration-none text-dark"
          onClick={() => navigate(appPaths.vms.list)}
        >
          <FontAwesomeIcon icon="arrow-left" className="me-1" />
          Back
        </Button>
        <span className="text-400 fs--1">|</span>
        <span className="text-700 fs--1">
          {loading ? '...' : vm.name || vm.id}
        </span>
        <span className="text-400 fs--1">|</span>
        <div className="d-flex gap-2">
          <Button
            variant="link"
            size="sm"
            className={`p-1 text-dark text-decoration-none ${
              activeView === 'screen' ? 'border border-dark rounded' : ''
            }`}
            onClick={() => setActiveView('screen')}
            title="Cloud Desktop Screen"
          >
            <FontAwesomeIcon icon="desktop" />
          </Button>
          <Button
            variant="link"
            size="sm"
            className={`p-1 text-dark text-decoration-none ${
              activeView === 'openclaw' ? 'border border-dark rounded' : ''
            }`}
            onClick={() => setActiveView('openclaw')}
            title="OpenClaw"
          >
            <FontAwesomeIcon icon="robot" />
          </Button>
          <Button
            variant="link"
            size="sm"
            className={`p-1 text-dark text-decoration-none ${
              activeView === 'files' ? 'border border-dark rounded' : ''
            }`}
            onClick={() => setActiveView('files')}
            title="Files"
          >
            <FontAwesomeIcon icon="folder-open" />
          </Button>
        </div>
        <span className="text-400 fs--1">|</span>
        <Button
          variant="link"
          size="sm"
          className="p-0 text-dark"
          onClick={toggleFullscreen}
          title="Toggle fullscreen"
        >
          <FontAwesomeIcon icon="expand-arrows-alt" />
        </Button>
      </div>

      <div ref={viewsRef}>
        {/* VNC Desktop */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            visibility: activeView === 'screen' ? 'visible' : 'hidden'
          }}
        >
          <Playground vmId={id} />
        </div>

        {/* OpenClaw Control UI */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            visibility: activeView === 'openclaw' ? 'visible' : 'hidden'
          }}
        >
          <OpenClawPanel vmId={id} />
        </div>

        {/* File Browser */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            visibility: activeView === 'files' ? 'visible' : 'hidden'
          }}
        >
          <FileBrowserPanel vmId={id} />
        </div>
      </div>
    </div>
  );
};

export default VM;
